/* eslint-disable react-hooks/exhaustive-deps */
import {
  ActionIcon,
  Anchor,
  AppShell,
  Autocomplete,
  Box,
  Container,
  Group,
  Header,
  Image,
  Navbar,
  Skeleton,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconBookmark,
  IconChecklist,
  IconCircleX,
  IconClipboardCheck,
  IconCloudUpload,
  IconFileCheck,
  IconFiles,
  IconFileSearch,
  IconHelp,
  IconInbox,
  IconLayoutDashboard,
  IconManualGearbox,
  IconMessageQuestion,
  IconPackage,
  IconPackageOff,
  IconQuestionMark,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import useRotatingPlaceholder, {
  SEARCH_PLACEHOLDERS,
} from '../hooks/useRotatingPlaceholder';
import useSearchDocument from '../hooks/useSearchDocument';

//Images
import logo from '../assets/images/logo/CMYK/logo/png/Juice_Primary_Logo.png';

//config

// Custom Components
import { UserMenu } from '../components/_userMenu';
import TableView from '../components/DataGrid2';
import JuiceAssistant from '../components/JuiceAssistant';
import LoadingScreen from '../components/LoadingScreen';
import NavigationLinks from '../components/NavigationLinks';

// Custom Adappters
import { useNavigate, useSearchParams } from 'react-router-dom';
import TransactionAdapter from '../adapters/transactionAdapter';
import Dashboard from '../components/Dashboard';
import SearchResultItem from '../components/SearchResultItem';
import docStatusMap from '../constants/docStatusMap';

// import ItemView from "../components/ItemView";
// import pdfDoc from "../pdf/7fbe4c24-fdfb-4f06-a69b-ba8b52d68233.pdf"
// import export1 from "../export/Electricity-Export.csv"
// import export2 from "../export/Water-Export.csv"

const ContentRenderer = ({
  isLoading,
  view,
  data,
  fetchData,
  transactionViews,
}) => {
  if (isLoading) {
    return <LoadingScreen height="calc(100dvh - 206px)" />;
  }

  switch (true) {
    case view === docStatusMap.dashboard:
      return <Dashboard />;

    default:
      return (
        <TableView
          data={data}
          view={view}
          fetchData={fetchData}
          transactionViews={transactionViews}
        />
      );
  }
};

function Layout({ setTheme }) {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [view, setView] = useState('');
  const [transactionViews, setTransactionViews] = useState([]);
  const [navbarViewData, setNavbarViewData] = useState([]);
  // const [logoIsLoading, setLogoIsLoading] = useState(true);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  const [searchResults, searchError, searchInProgress] =
    useSearchDocument(debouncedSearch);
  const searchPlaceholder = useRotatingPlaceholder(SEARCH_PLACEHOLDERS, {
    paused: searchFocused || !!searchValue,
  });

  useEffect(() => {
    getViewDetails();
  }, []);

  useEffect(() => {
    // As we want to show "ManualQ" as the initially selected tab/screen
    // when `navbarViewData` (nav menu items) is loaded.
    loadDefaultQueue();
  }, [navbarViewData]);

  const loadDefaultQueue = () => {
    if (navbarViewData.length) {
      let queueName = 'dashboard'; // Default Queue
      let queueId = docStatusMap[queueName]; // Queue Id for Default the queue.

      // Check if URL contains the desired queue name.
      const prefQueueName = searchParams.get('q');

      // If yes, check if the queue name in the query param is valid
      // and such queue exists i.e. `prefQueueId` !== undefined.
      // For example, `abc.com/documents?q=abcQ` contains the queue name "abcQ"
      // but no such queue exists in our `docStatusMap`.
      const prefQueueId = docStatusMap[prefQueueName];

      if (prefQueueId) {
        // Update queue details if user's preferred queue exists..
        queueName = prefQueueName;
        queueId = prefQueueId;
      }

      setSearchParams((params) => {
        params.set('q', queueName);
        return params;
      });

      fetchData(queueId);

      // Find the right index of the preferred queue and
      // update the `activeTabIndex` state which is used by `NavigationLinks`
      // component to show selected tab.
      navbarViewData.some((navItem, index) => {
        if (navItem.status && navItem.status === queueId) {
          setActiveTabIndex(index);
          return true;
        }
        return false;
      });

      setView(queueId);
    }
  };

  const getViewDetails = async () => {
    const companyId = localStorage.getItem('companyId');
    let viewDetails = await TransactionAdapter.getTransactionViews(companyId);
    setTransactionViews(viewDetails);

    let navbarData = [];
    for (let index = 0; index < viewDetails.length; index++) {
      const element = viewDetails[index];
      if (
        element.navBarDetails !== null &&
        element.navBarDetails !== undefined
      ) {
        let object = element.navBarDetails;
        object.icon = getIconByView(element.navBarDetails.label);
        object.queueOrder = element.queueOrder;
        object.isDashboard = element.statusId === docStatusMap.dashboard;
        navbarData.push(element.navBarDetails);
      }
    }

    const sortedQueue = orderByNumericProperty(navbarData, 'queueOrder');
    setNavbarViewData(sortedQueue);
  };

  function orderByNumericProperty(arr, prop) {
    return arr.sort((a, b) => a[prop] - b[prop]);
  }

  const getIconByView = (viewLabel) => {
    switch (viewLabel.toUpperCase()) {
      case 'DASHBOARD':
        return IconLayoutDashboard;
      case 'ZERO Q':
        return IconInbox;
      case 'MANUAL':
        return IconAlertCircle;
      case 'NO MATCHING PO':
        return IconFileSearch;
      case 'GRV INVOICES':
        return IconPackage;
      case 'NO MATCHING GRV':
        return IconPackageOff;
      case 'DUPLICATE':
        return IconFiles;
      case 'QUERIES':
        return IconMessageQuestion;
      case 'BATCH':
        return IconChecklist;
      case 'COMPLETE':
        return IconFileCheck;
      case 'SAP WAITING QUEUE':
        return IconCloudUpload;
      case 'SAP QUERY':
        return IconAlertTriangle;
      case 'SERVICE INVOICES':
        return IconClipboardCheck;
      case 'REJECTED':
        return IconCircleX;
      case 'SQUASHED':
        return IconTrash;
      case 'BOOKMARKS':
        return IconBookmark;
      default:
        return IconManualGearbox;
    }
  };

  const fetchData = async (newView) => {
    if (newView === undefined) {
      newView = view;
    }
    setIsLoading(true);
    if (newView !== docStatusMap.bookmarks) {
      const data = {
        status: newView,
      };
      const foundTransactions = await TransactionAdapter.getTransactions(data);
      setData(foundTransactions);
    } else {
      const bookmarkedDocs = await TransactionAdapter.getBookmarkedDocs();
      setData(bookmarkedDocs);
    }
    setIsLoading(false);
  };

  const handleNavLinkClick = (index) => {
    const queueId = navbarViewData[index].status;

    // Avoid fetching and re-rendering when user clicks on same nav menu
    if (view === queueId) return;
    fetchData(queueId);
    setView(queueId);

    setSearchParams(() => {
      const newParams = new URLSearchParams();
      const queueParamKey = Object.entries(docStatusMap).find(
        ([key, value]) => value === queueId,
      )?.[0];

      if (queueParamKey) {
        newParams.set('q', queueParamKey);
      }

      return newParams;
    });
  };

  const setDefaultView = () => {
    window.location.href = '/';
  };

  const handleSearchSelection = (searchItem) => {
    navigate(`/documents/${searchItem.value}`);
  };

  return (
    <AppShell
      styles={{
        main: {
          background:
            theme.colorScheme === 'dark'
              ? theme.colors.dark[8]
              : theme.colors.gray[1],
        },
      }}
      navbarOffsetBreakpoint="sm"
      asideOffsetBreakpoint="sm"
      header={
        <Header height={64} py="xs">
          <Container fluid>
            <Group position="apart">
              {/* Logo */}
              <Image
                width={150}
                maw={150}
                mt="-4px"
                radius="md"
                src={logo}
                alt="Logo"
                onClick={setDefaultView}
              />

              {/* Autocomplete search input */}
              <Autocomplete
                placeholder={searchPlaceholder}
                mx="auto"
                icon={<IconSearch size={18} />}
                // Why `filter={() => true}`?
                // Check https://github.com/orgs/mantinedev/discussions/822
                filter={() => true}
                maxDropdownHeight="200px"
                data={searchResults.map((item) => ({
                  po_number: item.Document.po_number,
                  invoice_number: item.Document.invoice_number,
                  supplier: item.Document.supplier.name,
                  queue: item.state,
                  value: item._id,
                }))}
                itemComponent={SearchResultItem}
                value={searchValue}
                onChange={(value) => setSearchValue(value)}
                onItemSubmit={(value) => handleSearchSelection(value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                rightSectionWidth={36}
                rightSection={
                  <Tooltip
                    withArrow
                    multiline
                    width={300}
                    position="bottom"
                    label={
                      <Box>
                        <Box mb={6} style={{ fontWeight: 600 }}>
                          Search tips
                        </Box>
                        <Box mb={4}>
                          - Search by PO #, invoice #, or supplier name
                        </Box>
                        <Box mb={4}>
                          - Narrow a field: PO: 614814 · INV: IN000759 · SUP:
                          ita security
                        </Box>
                        <Box>
                          - Combine with ; (semicolon) e.g. PO: 614814; INV:
                          IN000759; SUP: ita security
                        </Box>
                      </Box>
                    }>
                    <ActionIcon
                      size="sm"
                      radius="xl"
                      variant="subtle"
                      color="gray"
                      aria-label="Search tips"
                      // Keep input focus when opening the tip
                      onMouseDown={(event) => event.preventDefault()}>
                      <IconQuestionMark size={14} />
                    </ActionIcon>
                  </Tooltip>
                }
                // Nothing Found Logic
                //  1. Show "Searching..." when search request is in progress.
                //  2. Show "Nothing found..." when search query is there in the search
                //      input field but search request has been completed with no search result.
                //  3. Hide the "Nothing found" element in other cases...
                nothingFound={
                  searchInProgress
                    ? 'Searching...'
                    : searchValue && !searchInProgress
                      ? 'Nothing found...'
                      : ''
                }
                style={{ width: '60%', maxWidth: 600 }}
              />

              {/* PC API Status Component */}
              {/* <PCApiStatus /> */}

              <Anchor
                href={`https://forms.clickup.com/9012423091/f/8cjxadk-10832/JZ02YUGZ2YZM42MWLH?Creators%20Email%20Address=${localStorage.getItem('userEmail') || ''}&Creator%20Name%20and%20Surname=${localStorage.getItem('userName') || ''}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  textDecoration: 'none',
                  color: '#339af0',
                  fontWeight: 500,
                  fontSize: '14px',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(51, 154, 240, 0.1)',
                    color: '#228be6',
                  },
                }}>
                <IconHelp size={18} />
                REQUEST SUPPORT
              </Anchor>
            </Group>
          </Container>
        </Header>
      }
      navbar={
        <Navbar hiddenBreakpoint="sm" hidden={true} width={{ base: 240 }}>
          <Navbar.Section grow mt="md">
            {navbarViewData.length > 0 && (
              <NavigationLinks
                setView={handleNavLinkClick}
                navbarData={navbarViewData}
                activeTabIndex={activeTabIndex}
              />
            )}

            {/* Shows nav-menu skeleton when loading... */}
            {navbarViewData.length === 0 && (
              <Box mx="sm">
                {[...Array(13)].map((_, index) => (
                  <Skeleton key={index} height={35} mb="sm" />
                ))}
              </Box>
            )}
          </Navbar.Section>
          <Navbar.Section>
            <UserMenu setTheme={setTheme} />
          </Navbar.Section>
        </Navbar>
      }>
      <ContentRenderer
        isLoading={isLoading}
        view={view}
        data={data}
        fetchData={fetchData}
        transactionViews={transactionViews}
      />
      {/*
        Mounted inside the signed-in shell, not at app root, so it is only reachable by an
        authenticated user whose data scope the api can derive. It calls our own
        /juiceAsk route; the RAG proxy secret and the scope are added server-side and the
        browser never holds either.
      */}
      <JuiceAssistant />
    </AppShell>
  );
}

export default Layout;
